/**
 * EPIC-048 / 48.3 — Favoris : source de vérité frontend des routes
 * favorisables (§28.D) et libellés FR des filtres connus (§28.E).
 *
 * Aligné mot pour mot avec `backend/src/Services/UserFavoriteWhitelist.php`
 * (ALLOWED_ROUTES). Tout ajout doit être propagé des deux côtés.
 *
 * Pour les filtres : seules les clés/valeurs présentes dans `FILTER_LABELS`
 * sont traduites. Toute clé inconnue retombe sur un rendu brut « clé : valeur ».
 */

export const ROUTE_LABELS = {
  "/devis.html": "Devis",
  "/factures.html": "Factures émises",
  "/factures-recues.html": "Factures reçues",
  "/paiements.html": "Transactions",
  "/paiements-en-attente.html": "Transactions à traiter",
  "/clients.html": "Clients",
  "/catalogue.html": "Catalogue",
  "/documents-liste.html": "Documents publics",
  "/caisse.html": "Caisse espèces",
  "/finances.html": "Finances",
  "/suivi.html": "Suivi",
  "/pilotage.html": "Pilotage",
  "/registres.html": "Registres légaux",
  "/declarations.html": "Déclarations",
  "/coffre.html": "Coffre fort",
  "/notifications.html": "Notifications",
  "/calendrier.html": "Calendrier",
};

/**
 * Libellés FR des valeurs de filtres connues. Structure :
 *   { <cléFiltre>: { <valeur>: "Libellé FR" } }
 *
 * Une clé absente ou une valeur absente → fallback « clé : valeur » brut.
 */
export const FILTER_LABELS = {
  status: {
    draft: "Brouillons",
    sent: "Envoyées",
    overdue: "En retard",
    paid: "Payées",
    cancelled: "Annulées",
    pending: "En attente",
    accepted: "Acceptés",
    refused: "Refusés",
    active: "Actifs",
    archived: "Archivés",
  },
  type: {
    devis: "Devis",
    facture: "Factures",
    avoir: "Avoirs",
    acompte: "Acomptes",
  },
};

const MAX_LABEL_LENGTH = 80;
const TRUNCATE_AT = 77;
const ELLIPSIS = "…";

export function isRouteAllowed(path) {
  return Object.prototype.hasOwnProperty.call(ROUTE_LABELS, path);
}

/**
 * Libellé d'une valeur de filtre : trad FR si connue, sinon valeur brute.
 *
 * @param {string} key
 * @param {string|string[]} value
 * @returns {string}
 */
function formatFilterValue(key, value) {
  if (Array.isArray(value)) {
    return value.map((v) => formatFilterValue(key, v)).join(", ");
  }
  const dict = FILTER_LABELS[key];
  if (dict && Object.prototype.hasOwnProperty.call(dict, value)) {
    return dict[value];
  }
  if (key === "period" && /^\d{4}Q[1-4]$/.test(value)) {
    return `T${value.slice(5)} ${value.slice(0, 4)}`;
  }
  return value;
}

/**
 * Suffixe lisible pour un objet de filtres normalisés. Concatène
 * « <Libellé valeur> » si la clé a un dictionnaire et que la valeur est
 * connue, sinon « clé : valeur » brut. Plusieurs filtres séparés par
 * « , ».
 *
 * @param {Object<string, string|string[]>} queryParams
 * @returns {string}
 */
function buildFilterSuffix(queryParams) {
  const parts = [];
  for (const [key, value] of Object.entries(queryParams)) {
    const dict = FILTER_LABELS[key];
    if (dict || key === "period") {
      parts.push(formatFilterValue(key, value));
    } else {
      const displayed = Array.isArray(value) ? value.join(", ") : value;
      parts.push(`${key} : ${displayed}`);
    }
  }
  return parts.join(", ");
}

/**
 * Libellé auto-généré pour un favori : « <Route> — <Suffixe filtres> ».
 * Tronqué à 77 caractères + « … » si total > 80.
 *
 * @param {string} routePath - chemin whitelisté (sinon retourne `""`)
 * @param {Object<string, string|string[]>} queryParams - déjà normalisés
 * @returns {string}
 */
export function buildAutoLabel(routePath, queryParams) {
  const base = ROUTE_LABELS[routePath];
  if (!base) {
    return "";
  }
  const suffix = buildFilterSuffix(queryParams);
  const full = suffix ? `${base} — ${suffix}` : base;
  if (full.length <= MAX_LABEL_LENGTH) {
    return full;
  }
  return full.slice(0, TRUNCATE_AT) + ELLIPSIS;
}
